import { useMemo } from 'react';

import { useConfigSlice } from '../slices/config/selectors';
import { useSettingsSlice } from '../slices/settings/selectors';

/**
 * Use to get a constantly (every 3 seconds) updated communication delay in ms which is in the range
 * of min and max values set by the game. Communication delay generally is a sum of studio latency and
 * the video stream latency.
 * @returns Communication delay in milliseconds
 */
export const useCommunicationDelay = (): number => {
  const timings = useConfigSlice.getTimings();
  const videoDelayBoundaryMin = timings?.videoDelayBoundaryMin;
  const videoDelayBoundaryMax = timings?.videoDelayBoundaryMax;
  const studioLatency = timings?.studioLatency;
  const gameCommunicationDelay = timings?.gameCommunicationDelay;
  const streamDynamicLatency = useSettingsSlice.getStreamLatency();

  return useMemo(() => {
    if (studioLatency && videoDelayBoundaryMax && videoDelayBoundaryMin) {
      let clampedLatency = streamDynamicLatency + studioLatency;
      if (clampedLatency > videoDelayBoundaryMax) {
        clampedLatency = videoDelayBoundaryMax;
      }
      if (clampedLatency < videoDelayBoundaryMin) {
        clampedLatency = videoDelayBoundaryMin;
      }
      return clampedLatency;
    } else {
      return gameCommunicationDelay ?? 2000;
    }
  }, [videoDelayBoundaryMin, videoDelayBoundaryMax, studioLatency, streamDynamicLatency, gameCommunicationDelay]);
};
